import {
  ConsignmentApiModel,
  ConsignmentParcelItemApiModel,
  Dimensions,
  SmartSendItemApiModel
} from '~/lib/api/smartsend/types';
import { SumNumber, onlyUnique } from '../../../../lib/helpers/ArrayHelper';
import { clearLocalUnitsEntryFields } from '../../../../lib/helpers/DimensionUnits';
import { uid } from '../../../../lib/helpers/uid';
import { QuantityValueSummary } from '../QuantityValueSummary';

export function GetConsignmentFromItem(item: SmartSendItemApiModel): ConsignmentApiModel {
  if (item.Consignment) {
    AddIdsToConsignment(item.Consignment);
    return item.Consignment;
  }

  return null;
}

export function SanitiseConsignmentForSave(item: SmartSendItemApiModel) {
  if (item.Consignment?.Parcels?.length > 0) {
    // Merges duplicate rows back together so the saved payload is as compact as the data allows.
    //
    // This used to also drop the whole consignment when the serialised JSON exceeded 2400
    // characters, silently turning a carefully arranged multi-parcel shipment into a single parcel
    // with no feedback. The cap is gone.
    //
    // It was NOT arbitrary, contrary to what this comment previously claimed. The consignment is
    // persisted as a JSON string inside Meta["ConsignmentMeta"] - see UpdateMarketplaceItemHandler
    // on the write side and ConsignmentFilter on the read side - so 2400 was a Meta value length
    // limit. That limit is being lifted API-side so all lengths work; removing the guard here is
    // correct, but it depends on that API change landing first. Silently discarding the user's
    // arrangement was never the right response to a length limit either way.
    ConsolidateParcelQuantities(item.Consignment);
  }
}

function generateKey(o: ConsignmentParcelItemApiModel): string {
  return `${o.Id}|${o.Description}|${o.Sku ?? ''}|${o.TariffCode ?? ''}|${o.CountryOfManufacture ?? ''}`;
}

export function ConsolidateParcelQuantities(c: ConsignmentApiModel) {
  c.Parcels.forEach((p) => {
    // A parcel can arrive from the server with no Contents property at all - AddIdsToConsignment below
    // already guards for it with `p.Contents?.forEach`. This runs on every save via
    // SanitiseConsignmentForSave, so an unguarded read here would throw on exactly that data. Writing
    // the array back at the end also normalises the shape for everything downstream.
    const contents = p.Contents ?? [];

    // Manual rows never merge - even with each other, since two manually added rows are
    // distinct user entries, not duplicates of the same underlying order line.
    const manualContents = contents.filter((x) => x.Manual);
    const consolidatable = contents.filter((x) => !x.Manual);

    const newOrders: ConsignmentParcelItemApiModel[] = [];

    // Group by id and any per-parcel customs edits the user might have entered, so rows only
    // merge when they are genuinely identical.
    consolidatable
      .map(generateKey)
      .filter(onlyUnique)
      .forEach((key) => {
        const list = consolidatable.filter((x) => generateKey(x) === key);
        const value = SumNumber(list.map((l) => l.Value));
        const qty = SumNumber(list.map((l) => l.Quantity));
        newOrders.push({ ...list[0], Quantity: qty, Value: value });
      });

    p.Contents = newOrders.concat(manualContents);
  });
}

export function SplitConsignmentWeight(d: Dimensions, c: ConsignmentApiModel): ConsignmentApiModel {
  if (!c || !d?.Weight) {
    return c;
  }

  const s = QuantityValueSummary(d.Weight, c.Parcels.length);
  c.Parcels.forEach((p, i) => {
    const weight = i == 0 ? s.valueForFirstItem : s.valueForOtherItems;

    // Assign a fresh object rather than into p.Dimensions.Weight - a parcel can arrive without any
    // Dimensions (MapParcels reads `consignmentParcel.Dimensions?.Weight`, so the API tolerates it),
    // and writing through an undefined property throws.
    p.Dimensions = { ...p.Dimensions, Weight: weight };

    // A divided weight was never typed by anyone, so an as-entered original on this parcel would now
    // display a number contradicting the weight actually stored and booked. Drop it and let display
    // convert. A single parcel takes the whole weight undivided, so its original is still true.
    if (weight !== d.Weight && p.LocalUnits?.originalWeightLb != null) {
      p.LocalUnits = clearLocalUnitsEntryFields(p.LocalUnits, ['weight']);
    }
  });

  return c;
}

export function AddIdsToConsignment(c: ConsignmentApiModel) {
  c.Parcels?.forEach((p) => {
    p.UID = uid();
    p.Contents?.forEach((o) => {
      o.UID = uid();
    });
  });
}
